import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OTPVerification.css';

const OTPVerification = ({ 
    email, 
    username, 
    onVerificationComplete, 
    onCancel,
    title = "Email Verification",
    subtitle = "Enter the verification code sent to your email"
}) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    // Auto-focus first input on mount
    useEffect(() => {
        document.getElementById('otp-0')?.focus();
    }, []);

    // Resend timer countdown
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    const handleInputChange = (index, value) => {
        // Only allow digits
        if (!/^[0-9]?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            document.getElementById(`otp-${index + 1}`)?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace to go to previous input
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`)?.focus();
        }
        // Handle paste
        else if (e.key === 'Enter') {
            handleVerify();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text');
        const digits = paste.replace(/\\D/g, '').slice(0, 6);
        
        if (digits.length === 6) {
            const newOtp = digits.split('');
            setOtp(newOtp);
            document.getElementById('otp-5')?.focus();
        }
    };

    const handleVerify = async () => {
        const otpCode = otp.join('');
        
        if (otpCode.length !== 6) {
            setError('Please enter the complete 6-digit code');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post('auth/verify-otp', {
                email,
                otp: otpCode
            });

            if (response.data.success) {
                setSuccess('Email verified successfully!');
                setTimeout(() => {
                    onVerificationComplete?.(true);
                }, 1500);
            } else {
                setError(response.data.message || 'Invalid verification code');
                // Clear OTP on error
                setOtp(['', '', '', '', '', '']);
                document.getElementById('otp-0')?.focus();
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Verification failed. Please try again.');
            setOtp(['', '', '', '', '', '']);
            document.getElementById('otp-0')?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axios.post('auth/resend-otp', {
                email,
                username
            });

            if (response.data.success) {
                setSuccess('Verification code resent to your email');
                setResendTimer(60); // 60 second cooldown
                setOtp(['', '', '', '', '', '']);
                document.getElementById('otp-0')?.focus();
            } else {
                setError(response.data.message || 'Failed to resend code');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to resend code. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="otp-verification-overlay">
            <div className="otp-verification-modal">
                <div className="otp-header">
                    <h2>{title}</h2>
                    <p>{subtitle}</p>
                    <div className="email-display">
                        <span>{email}</span>
                    </div>
                </div>

                <div className="otp-form">
                    <div className="otp-inputs">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                id={`otp-${index}`}
                                type="text"
                                value={digit}
                                onChange={(e) => handleInputChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                maxLength={1}
                                className={`otp-input ${error ? 'error' : ''} ${success ? 'success' : ''}`}
                                disabled={loading}
                            />
                        ))}
                    </div>

                    {error && (
                        <div className="message error-message">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="message success-message">
                            {success}
                        </div>
                    )}

                    <div className="otp-actions">
                        <button
                            onClick={handleVerify}
                            disabled={loading || otp.join('').length !== 6}
                            className="verify-btn"
                        >
                            {loading && (
                                <span className="loading-spinner"></span>
                            )}
                            {loading ? 'Verifying...' : 'Verify Email'}
                        </button>

                        <div className="resend-section">
                            <p>Didn't receive the code?</p>
                            <button
                                onClick={handleResend}
                                disabled={resendLoading || resendTimer > 0}
                                className="resend-btn"
                            >
                                {resendLoading ? 'Sending...' : 
                                 resendTimer > 0 ? `Resend in ${resendTimer}s` : 
                                 'Resend Code'}
                            </button>
                        </div>

                        <button
                            onClick={() => onCancel?.()}
                            className="cancel-btn"
                            disabled={loading}
                        >
                            Back
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OTPVerification;
